import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import BrandCombobox from '@/components/BrandCombobox';
import { 
  Car, AlertTriangle, CheckCircle2, Loader2, Search, 
  Sparkles, ArrowRight
} from 'lucide-react';

// Cache local para marcas
const brandsCache = { data: null };

export default function OnboardingVehicle() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState({ brand: '', model: '', plate: '', vehicle_type: '' });
  const [selectedModel, setSelectedModel] = useState(null);
  const [needsManualSelection, setNeedsManualSelection] = useState(false);
  
  // Autocomplete do modelo
  const [modelInput, setModelInput] = useState('');
  const [modelSuggestions, setModelSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingModels, setSearchingModels] = useState(false);
  const modelInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    checkExistingVehicle();
    fetchBrands();
  }, [user, navigate]);

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          modelInputRef.current && !modelInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkExistingVehicle = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/client/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.has_vehicle) {
        navigate((res.data.has_active_plan || res.data.has_pending_plan) ? '/client/dashboard' : '/client/onboarding/subscription-data', { replace: true });
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    }
  };

  const fetchBrands = async () => {
    if (brandsCache.data) {
      setBrands(brandsCache.data);
      setBrandsLoading(false);
      return;
    }
    setBrandsLoading(true);
    try {
      const res = await axios.get(`${API}/vehicles/v2/brands?limit=100`);
      brandsCache.data = res.data.brands;
      setBrands(res.data.brands);
    } catch (error) {
      try {
        const res = await axios.get(`${API}/vehicles/brands`);
        setBrands(res.data.brands);
      } catch (err) {
        toast.error('Erro ao carregar marcas');
      }
    } finally {
      setBrandsLoading(false);
    }
  };

  const handleBrandChange = (brand) => {
    setForm({ ...form, brand, model: '', vehicle_type: '' });
    setModelInput('');
    setModelSuggestions([]);
    setSelectedModel(null);
    setNeedsManualSelection(false);
  };

  // Buscar modelos com debounce
  const searchModels = async (query) => {
    if (!form.brand || query.length < 1) {
      setModelSuggestions([]);
      return;
    }
    
    setSearchingModels(true);
    try {
      const res = await axios.get(`${API}/vehicles/v2/models`, {
        params: { brand: form.brand, q: query, limit: 5 }
      });
      setModelSuggestions(res.data.models);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Erro ao buscar modelos:', error);
    } finally {
      setSearchingModels(false);
    }
  };

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (modelInput && form.brand) {
        searchModels(modelInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [modelInput, form.brand]);

  const handleModelInputChange = (e) => {
    const value = e.target.value;
    setModelInput(value);
    setForm({ ...form, model: '', vehicle_type: '' });
    setSelectedModel(null);
    setNeedsManualSelection(false);
    
    if (value.length === 0) {
      setModelSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectModel = (modelData) => {
    setModelInput(modelData.modelo);
    setForm({ ...form, model: modelData.modelo, vehicle_type: modelData.vehicle_type || '' });
    setSelectedModel(modelData);
    setShowSuggestions(false);
    
    if (modelData.vehicle_type) {
      setNeedsManualSelection(false);
    } else {
      setNeedsManualSelection(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.brand || !form.model || !form.plate) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (needsManualSelection && !form.vehicle_type) {
      toast.error('Selecione o porte do veículo');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const vehicleType = selectedModel?.vehicle_type || form.vehicle_type;
      
      const res = await axios.post(`${API}/client/vehicle`, {
        brand: form.brand,
        model: form.model,
        plate: form.plate.toUpperCase(),
        vehicle_type: vehicleType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Veículo cadastrado: ${res.data.vehicle_type_display}!`);
      // Salvar apenas dados do veículo (planos serão buscados da API na tela de planos)
      sessionStorage.setItem('identified_vehicle', JSON.stringify(res.data.vehicle));
      sessionStorage.setItem('vehicle_type_display', res.data.vehicle_type_display);
      navigate('/client/onboarding/subscription-data');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar veículo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-primary">Etapa 1 de 3</span>
            <span className="text-xs text-muted-foreground">Cadastrar Veículo</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: '33%' }} />
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-primary/10 p-3 rounded-xl">
            <Car className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Cadastre seu Veículo</h1>
            <p className="text-sm text-muted-foreground">Selecione marca e digite o modelo</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Marca - Usando BrandCombobox resiliente */}
          <div>
            <Label htmlFor="brand">Marca *</Label>
            <BrandCombobox
              brands={brands}
              value={form.brand}
              onChange={handleBrandChange}
              loading={brandsLoading}
              placeholder="Selecione a marca..."
              testId="vehicle-brand-select"
            />
          </div>

          {/* Modelo - Input com Autocomplete */}
          <div className="relative">
            <Label htmlFor="model">Modelo *</Label>
            <div className="relative">
              <Input
                ref={modelInputRef}
                id="model"
                placeholder={form.brand ? "Digite o modelo (ex: Gol, Civic)..." : "Primeiro selecione a marca"}
                value={modelInput}
                onChange={handleModelInputChange}
                onFocus={() => modelInput && setShowSuggestions(true)}
                disabled={!form.brand}
                className="pr-10"
                autoComplete="off"
                data-testid="vehicle-model-input"
              />
              {searchingModels && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
              )}
            </div>
            
            {/* Dropdown de sugestões */}
            {showSuggestions && modelSuggestions.length > 0 && (
              <div 
                ref={suggestionsRef}
                className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-[200px] overflow-auto"
              >
                {modelSuggestions.map((m) => (
                  <button
                    key={m.modelo}
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-slate-100 flex items-center justify-between"
                    onClick={() => handleSelectModel(m)}
                  >
                    <span className="font-medium">{m.modelo}</span>
                    <span className="text-xs text-muted-foreground">{m.vehicle_type_display}</span>
                  </button>
                ))}
              </div>
            )}
            
            {/* Nenhum resultado */}
            {showSuggestions && modelInput.length >= 2 && modelSuggestions.length === 0 && !searchingModels && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg p-3 text-sm text-muted-foreground">
                Modelo não encontrado. Selecione o porte manualmente.
              </div>
            )}
          </div>

          {/* Veículo identificado */}
          {selectedModel?.vehicle_type && !needsManualSelection && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-sm text-green-800">
                <strong>Identificado:</strong> {selectedModel.vehicle_type_display}
              </AlertDescription>
            </Alert>
          )}

          {/* Seleção manual */}
          {(needsManualSelection || (modelInput.length >= 2 && !selectedModel)) && (
            <>
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-sm text-amber-800">
                  Selecione o porte do veículo manualmente.
                </AlertDescription>
              </Alert>
              <div>
                <Label>Porte do Veículo *</Label>
                <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                  <SelectTrigger data-testid="vehicle-type-select">
                    <SelectValue placeholder="Selecione o porte..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Pequeno (Hatch, Sedan)</SelectItem>
                    <SelectItem value="suv">Médio (SUV, Crossover)</SelectItem>
                    <SelectItem value="pickup">Grande (Caminhonete)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Placa */}
          <div>
            <Label htmlFor="plate">Placa *</Label>
            <Input
              id="plate"
              placeholder="ABC-1234"
              value={form.plate}
              onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
              maxLength={8}
              required
              data-testid="vehicle-plate-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12"
            disabled={loading || !form.brand || (!form.model && !modelInput) || !form.plate || (needsManualSelection && !form.vehicle_type)}
            data-testid="vehicle-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Continuar <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>

        <div className="mt-6 pt-4 border-t">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>Digite o modelo base (ex: Gol, Civic, Corolla). Não inclua versões ou motorização.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
